"use client";

import { setHeaderIsShow } from "@/app/store/slices/general";
import { Fade, Grid2 as Grid, useMediaQuery, useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import Logo from "@v2/components/Logo";
import useOnLoadEvent from "@v2/hooks/useOnLoadEvent";
import useScrollYChange from "@v2/hooks/useScrollYChange";
import useTouchable from "@v2/hooks/useTouchable";
import { DeviceViewport } from "@v2/types";
import { usePathname } from "next/navigation";
import Link from "next/link";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import FlexBox from "../styled/FlexBox";
import { getHeader } from "./api";
import CartButton from "./CartButton";
import DesktopMenu, { DesktopMenuRef } from "./DesktopMenu";
import HeaderAuthArea from "./HeaderAuthArea";
import { HeaderLoading } from "./loading";
import MobileMenu from "./MobileMenu";
import ProfileButton from "./ProfileButton";
import SearchButton from "./SearchButton";
import AdminBarOrTopBar from "./AdminBarOrTopBar";
import { HEADER_HEIGHT, TOP_BAR_HEIGHT } from "./constants";
import { MenuItemType } from "./types";
import useMountLog from "./useMountLog";

const HIDDEN_PATHS: RegExp[] = [
    /^\/fa(?:\/v2)?\/products\/.+$/,
    /^\/fa\/v2\/profile\/.+$/,
]

interface IProps {
    xs?: boolean;
    md?: boolean;
    menuData: MenuItemType[] | null,
    menuUrl?: string,
    viewport?: DeviceViewport,
    showTopBar?: boolean,
    logoHref?: string,
}
const LOCALSTORAGE_KEY = "latest_menu";
const BLOG_LOCALSTORAGE_KEY = "latest_blog_menu";
export default function CsrHeader({ menuData, menuUrl, viewport, showTopBar, logoHref }: IProps) {
    useMountLog(menuUrl ? "CsrHeader(blog)" : "CsrHeader");
    const isBlogMenu = !!menuUrl;
    const storageKey = isBlogMenu ? BLOG_LOCALSTORAGE_KEY : LOCALSTORAGE_KEY;
    const [internalMenuData, setInternalMenuData] = useState<MenuItemType[]>(menuData ?? JSON.parse(localStorage.getItem(storageKey)) ?? [])
    useEffect(() => {
        if (!menuData) {
            getHeader(menuUrl).then((md) => {
                setInternalMenuData(md)
                localStorage.setItem(storageKey, JSON.stringify(md))
            })
        }
    }, [])
    const [showHeader, setShowHeader] = useState(true);
    const [isFloating, setIsFloating] = useState(true);
    const [isSubmenuOpen, setIsSubmenuOpen] = useState(false);

    const desktopMenuRef = useRef<DesktopMenuRef>(null)

    const theme = useTheme()
    const isSmallerThan320 = useMediaQuery(theme.breakpoints.down('xs2'))
    const isTouchable = useTouchable()
    const dispatch = useDispatch()
    const pathname = usePathname()
    const isSmallerThanTablet = viewport === "mobile";
    const isHiddenPath = HIDDEN_PATHS.some((p) => p.test(pathname));
    const shouldHideHeader = viewport === "mobile" && isHiddenPath;
    // const seoLinks = useMemo(() => {
    //     const links = new Map<string, string>();

    //     for (const menuItem of internalMenuData) {
    //         if (menuItem.href) {
    //             links.set(menuItem.href, menuItem.label);
    //         }

    //         for (const col of menuItem.children ?? []) {
    //             for (const child of col.children ?? []) {
    //                 if ("href" in child && child.href) {
    //                     links.set(child.href, child.type === "link" ? child.label : menuItem.label);
    //                 }
    //             }
    //         }
    //     }

    //     return Array.from(links.entries()).map(([href, label]) => ({ href, label }));
    // }, [internalMenuData]);

    const prevScrollYRef = useRef<number | null>(null);
    useScrollYChange((scrollY) => {
        if (shouldHideHeader) return;
        if (prevScrollYRef.current === null) prevScrollYRef.current = scrollY;

        if (isTouchable && desktopMenuRef?.current?.subMenuIsOpen()) {
            setIsSubmenuOpen(false)
            desktopMenuRef.current?.closeSubMenu?.()
        }

        const threshold = 6
        const whiteSpace = 116;
        const scrollingUp = prevScrollYRef.current > (scrollY + threshold);
        const scrollingDown = scrollY > (prevScrollYRef.current + threshold);
        const jumpedToTop = prevScrollYRef.current === 0 && scrollY === 0;

        setIsFloating(scrollY > 0)
        /**
         * init window scroll after reload or first load
         */
        if (scrollingUp || jumpedToTop) {
            // directing to top
            setShowHeader(true)
            dispatch(setHeaderIsShow(true))
        } else if (scrollingDown) {
            if (scrollY < whiteSpace) return;
            // directing to down
            if (!isSubmenuOpen) {
                setShowHeader(false)
                dispatch(setHeaderIsShow(false))
            }
        }
        prevScrollYRef.current = scrollY;
    }, [isTouchable, isSubmenuOpen, shouldHideHeader], {
        ttl: 100
    })

    useOnLoadEvent(() => {
        const { scrollY } = window
        if (scrollY === 0) {
            setIsFloating(false)
        }
    })
    // if (shouldHideHeader) {
    //     return (
    //         <Box
    //             component="nav"
    //             aria-label="header seo links"
    //             sx={{
    //                 position: "absolute",
    //                 width: 1,
    //                 height: 1,
    //                 p: 0,
    //                 m: "-1px",
    //                 overflow: "hidden",
    //                 clip: "rect(0, 0, 0, 0)",
    //                 border: 0,
    //                 whiteSpace: "nowrap",
    //             }}
    //         >
    //             {seoLinks.map((item) => (
    //                 <Box key={item.href} component={Link} href={item.href}>
    //                     {item.label}
    //                 </Box>
    //             ))}
    //         </Box>
    //     );
    // }
    if (shouldHideHeader) {
        return null;
    }

    return (
        // <NoSsr fallback={<HeaderLoading isAuth={isAuth} viewport={viewport} />}>
        <>
            {showTopBar && <AdminBarOrTopBar />}
            {!internalMenuData.length ? (
                <HeaderLoading viewport={viewport} showTopBar={showTopBar} />
            ) : (
            <HeaderComponent showTopBar={showTopBar} viewport={viewport}>
                <HeaderWrapper showTopBar={showTopBar}>
                    <HeaderInner show={showHeader} isFloating={isFloating} viewport={viewport}>
                    {isSmallerThanTablet ? (
                        // mobile 
                        <FlexBox sx={{
                            px: 1,
                            // pr: 1.5,
                            height: 1,
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}>
                            <FlexBox sx={{ alignItems: 'center', gap: 1 }}>
                                <MobileMenu data={internalMenuData} isFloating={isFloating} />
                                <SearchButton mode={isBlogMenu ? 'blog' : undefined} />
                                <HeaderAuthArea viewport={viewport} />
                            </FlexBox>

                            <Logo type={isSmallerThan320 ? "micro" : undefined} href={logoHref} />
                        </FlexBox>
                    ) : (
                        <Grid container alignItems={'center'} sx={{
                            maxWidth: 1400, margin: 'auto',
                            boxSizing: 'content-box', px: { xs: 0, sm: 2 },
                            [theme.breakpoints.down('sm')]: {
                                display: 'none'
                            }
                        }}>
                            <Grid size={{ sm: 6, md2: 2 }} order={{ sm: 1, md2: 1 }}>
                                <Logo type="mobile" href={logoHref} />
                            </Grid>
                            <Grid size={{ sm: 12, md2: 7.5 }} order={{ sm: 3, md2: 2 }}>
                                <DesktopMenu ref={desktopMenuRef} data={internalMenuData}
                                    onChangeSubmenuIsOpen={setIsSubmenuOpen}
                                    isFloating={isFloating} viewport={viewport} />
                            </Grid>
                            <Grid size={{ sm: 6, md2: 2.5 }} order={{ sm: 2, md2: 3 }}>

                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: 'flex-end', gap: 1 }}>
                                    <Fade in={true}>
                                        <Box>
                                            <SearchButton mode={isBlogMenu ? 'blog' : undefined} />
                                        </Box>
                                    </Fade>
                                    <HeaderAuthArea viewport={viewport} />
                                </Box>

                            </Grid>
                        </Grid>
                    )}
                    </HeaderInner>
                </HeaderWrapper>
            </HeaderComponent>
            )}
            {/* <Box
                component="nav"
                aria-label="header seo links"
                sx={{
                    display: 'none',
                    position: "absolute",
                }}
            >
                {seoLinks.map((item) => (
                    <Box key={item.href} component={Link} href={item.href}>
                        {item.label}
                    </Box>
                ))}
            </Box> */}
        </>
        // </NoSsr>
    );
}

const HeaderComponent = (props: {
    children: ReactNode,
    showTopBar?: boolean,
    viewport?: string,
}) => {
    const theme = useTheme()
    const isMobile = props.viewport === "mobile"
    const topBarOffset = props.showTopBar ? TOP_BAR_HEIGHT : 0
    return <Box sx={{
        height: isMobile ? (HEADER_HEIGHT.mobile + topBarOffset) : (HEADER_HEIGHT.desktop + topBarOffset),
        backgroundImage: `url('/assets/wm.webp')`,
        backgroundRepeat: 'repeat',
        backgroundSize: '165px',
        backgroundPosition: `0 ${48 + topBarOffset}px`,
        ...(!isMobile && {
            [theme.breakpoints.down('md2')]: {
                height: HEADER_HEIGHT.tablet + topBarOffset
            },
        }),
        [theme.breakpoints.down('sm')]: {
            height: HEADER_HEIGHT.mobile + topBarOffset
        }
    }} component="header" id="header">{props.children}</Box>
}

const HeaderWrapper = (props: {
    children: ReactNode,
    onHover?: () => void,
    showTopBar?: boolean,
}) => {
    return <Box onMouseEnter={props.onHover} sx={{
        zIndex: 999,
        height: 0,
        position: 'fixed',
        top: props.showTopBar ? TOP_BAR_HEIGHT : 0,
        right: 0,
        left: 0
    }}>
        {props.children}
    </Box>
}

const HeaderInner = (props: {
    children: ReactNode,
    show: boolean,
    isFloating?: boolean,
    viewport?: string,
}) => {
    const theme = useTheme()
    const isMobile = props.viewport === "mobile"
    return <Box className={props.show && "fixed_header_inner"} sx={{
        transition: '0.5s',
        width: '100%',
        bgcolor: '#fff',
        transform: 'translateY(-100%)',
        '&.fixed_header_inner': {
            transition: 'transform 0.5s cubic-bezier(0.22,0.61,0.36,1)',
            transform: 'translateY(0)',
            ...(props.isFloating ? {
                boxShadow: 'rgba(0, 0, 0, 0.09) 0px 4px 11px'
            } : {})
        },
        height: isMobile ? HEADER_HEIGHT.mobile : HEADER_HEIGHT.desktop,
        ...(!isMobile && {
            [theme.breakpoints.down('md2')]: {
                height: HEADER_HEIGHT.tablet
            },
        }),
        [theme.breakpoints.down('sm')]: {
            height: 56,
        }
    }}>
        {props.children}
    </Box>
}
